import { useMemo, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useGetInHouseIngredient } from "../../api/ingredients";
import { useGetWasteManagementWasteTypes } from "../../api/static-data";
import { PAGINATION_ITEMS_PER_PAGE } from "../../config/constants";
import { MEASUREMENT_TYPES } from "../../config/enums/MeasurementTypes";
import { AccordionTypes } from "../../helpers/AccordionHelper";
import { convertStringUnitToNumeric } from "../../utils/utils";
import { useUnitSync } from "../../hooks/useUnitSync";
import { useWasteCrud } from "../../hooks/useWasteCrud";
import { useWasteFormSync } from "../../hooks/useWasteFormSync";
import ConfirmModal from "../confirmViewModal/ConfirmModal";
import { Card, CardContent } from "../ui/common/card";
import { Form } from "../ui/common/form";
import { WasteAccordionHeader } from "./components/WasteAccordionHeader";
import { WastePagination } from "./components/WastePagination";
import { WasteRecordCard } from "./components/WasteRecordCard";
import { WasteRecordForm } from "./components/WasteRecordForm";
import { WASTE_VALIDATION } from "./wasteManagement.constants";
import { IWasteForm, WasteManagementProps, WasteToDelete } from "./wasteManagement.types";
import { mapWasteRecordFromBackend, sumWasteDistributions } from "./wasteManagement.utils";

import "./WasteManagement.scss";

export const WasteManagementContent = ({
  accordionType,
  wasteManagement,
  processId,
  sideStreamValue,
  refetchWasteData,
  type,
  supplierDataHandle = false,
  lockStatus = false,
  handleAccordion,
  onWasteManagementSave = () => {},
  disabled = false,
  parentControl
}: WasteManagementProps) => {
  const accordionEnabled = accordionType === AccordionTypes.WASTE_MANAGEMENT;

  const [currentPage, setCurrentPage] = useState(1);
  const [editingId, setEditingId] = useState<string | number | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [wasteToDelete, setWasteToDelete] = useState<WasteToDelete | null>(null);

  // ── Static / reference data ───────────────────────────────────────────────
  const { data: wasteManagementType = [] } = useGetWasteManagementWasteTypes();
  const { data: inHouseIngredients = [] } = useGetInHouseIngredient();

  /**
   * defaultTypeId: the first option's value from the management type dropdown.
   * Used as the pre-selected type for new draft records and for resetting the
   * draft form after a successful save.
   */
  const defaultTypeId = useMemo(
    () => (wasteManagementType?.[0]?.value ? String(wasteManagementType[0].value) : "1"),
    [wasteManagementType]
  );

  // ── Form initialisation ───────────────────────────────────────────────────
  // Pre-populate with mapped records so the form is hydrated before the
  // accordion-open useWasteFormSync effect fires for the first time.
  const form = useForm<IWasteForm>({
    defaultValues: {
      wastes: (wasteManagement || []).map(mapWasteRecordFromBackend),
      totalSide: null,
      sideStream: sideStreamValue,
      sideStreamUnit:
        wasteManagement?.length > 0 && wasteManagement[0].unit
          ? wasteManagement[0].unit
          : MEASUREMENT_TYPES.WEIGHT_KG
    },
    mode: "onChange"
  });

  const { control, reset, getValues, trigger, setValue } = form;

  const wastes = useWatch({ control, name: "wastes" });
  const targetKg = useWatch({ control, name: "sideStream" });
  const parentSideStreamUnitRaw = useWatch({
    control: parentControl || control,
    name: "sideStreamUnit"
  });
  const { fields, remove, update } = useFieldArray({ control, name: "wastes" });

  // ── Custom hooks ──────────────────────────────────────────────────────────

  /** Resolves the display unit ("kg" | "ton") for headers and formatted values. */
  const sideStreamUnit = useUnitSync({
    control,
    parentControl
  }) as "kg" | "ton";

  /**
   * Resets the React Hook Form state when the accordion opens or backend
   * data changes, always appending a blank draft row at the end.
   */
  useWasteFormSync({
    wasteManagement,
    accordionEnabled,
    sideStreamValue,
    defaultTypeId,
    reset,
    setValue,
    getValues,
    setEditingId,
    parentControl,
    parentSideStreamUnit: parentControl ? parentSideStreamUnitRaw : undefined
  });

  /**
   * Returns handleSaveWaste, handleAddWasteRecord, handleRemoveWasteRecord.
   * All business validation and API mutation logic lives in this hook.
   */
  const { handleSaveWaste, handleAddWasteRecord, handleRemoveWasteRecord } = useWasteCrud({
    processId,
    type,
    supplierDataHandle,
    defaultTypeId,
    getValues,
    setValue,
    trigger,
    remove,
    update,
    fieldsLength: fields.length,
    currentPage,
    setCurrentPage,
    editingId,
    setEditingId,
    refetchWasteData,
    onWasteManagementSave
  });

  /** Disable the form body when no side-stream target value has been set. */
  const isContentDisabled = disabled || !targetKg || Number(targetKg) === 0;

  /**
   * Live total of all saved records' distribution values (excludes the draft row).
   * When the accordion is open: reads from form state for immediate feedback as
   * the user types. When closed: reads from the wasteManagement prop so the
   * header still shows the correct total.
   */
  const totalDistribution = useMemo(() => {
    const targetUnit = convertStringUnitToNumeric(sideStreamUnit);

    if (accordionEnabled && wastes?.length > 0) {
      const savedRecords = wastes.slice(0, -1);
      const draftRecord = wastes[wastes.length - 1];
      const recordsForTotal = [...savedRecords];

      if (
        draftRecord?.sideStreamsDistribution &&
        parseFloat(draftRecord.sideStreamsDistribution) > 0
      ) {
        recordsForTotal.push(draftRecord);
      }

      return sumWasteDistributions(recordsForTotal, targetUnit, targetUnit);
    }

    if (!wasteManagement || !Array.isArray(wasteManagement)) return 0;
    return sumWasteDistributions(wasteManagement, targetUnit, targetUnit);
  }, [wastes, accordionEnabled, wasteManagement, sideStreamUnit]);

  /** true when totalDistribution differs from the target by more than the tolerance. */
  const hasDistributionMismatch = useMemo(() => {
    if (!targetKg || Number(targetKg) === 0) return false;
    return Math.abs(Number(targetKg) - totalDistribution) > WASTE_VALIDATION.DISTRIBUTION_TOLERANCE;
  }, [targetKg, totalDistribution]);

  const managementTypeOptions = useMemo(
    () =>
      (wasteManagementType || []).map((t: any) => ({
        value: String(t.value),
        text: t.text,
        label: t.text
      })),
    [wasteManagementType]
  );

  const ingredientOptions = useMemo(
    () =>
      (inHouseIngredients || []).filter((ingredient: any) => !ingredient.disabled).map((ingredient: any) => ({
        value: String(ingredient.value),
        text: ingredient.text,
        label: ingredient.text
      })),
    [inHouseIngredients]
  );

  // Exclude the draft row (always the last element) from the paginated list.
  const savedFields = fields.slice(0, -1);
  const totalPages = Math.max(1, Math.ceil(savedFields.length / PAGINATION_ITEMS_PER_PAGE));
  const paginatedFields = savedFields.slice(
    (currentPage - 1) * PAGINATION_ITEMS_PER_PAGE,
    currentPage * PAGINATION_ITEMS_PER_PAGE
  );

  return (
    <Card className="tw-border-border tw-pt-0 tw-pb-0 tw-mt-1">
      <WasteAccordionHeader
        recordCount={wasteManagement?.length ?? 0}
        targetKg={Number(targetKg) || 0}
        totalDistribution={totalDistribution}
        sideStreamUnit={sideStreamUnit}
        hasDistributionMismatch={hasDistributionMismatch}
        isErrorMessageVisible={accordionEnabled && hasDistributionMismatch}
        accordionEnabled={accordionEnabled}
        onClick={() => handleAccordion(AccordionTypes.WASTE_MANAGEMENT)}
      />

      {accordionEnabled && (
        <Form {...form}>
          <CardContent
            className={`tw-py-3.5 tw-px-3.5 tw-space-y-2 ${
              isContentDisabled ? "tw-opacity-50 tw-pointer-events-none" : ""
            }`}
          >
          {isContentDisabled && (
            <p className="tw-text-xs tw-text-muted-foreground tw-italic">
              Set a side stream target value to enable waste management records.
            </p>
          )}

          {/* Saved record list with inline edit support */}
          {fields.length > 1 && (
            <div className="tw-space-y-2">
              {paginatedFields.map((field, index) => {
                const globalIndex = (currentPage - 1) * PAGINATION_ITEMS_PER_PAGE + index;
                const isEditing = editingId === field._id;

                // Show the expanded edit form when this record is being edited.
                if (isEditing) {
                  return (
                    <WasteRecordForm
                      key={field.id}
                      control={control}
                      globalIndex={globalIndex}
                      field={field}
                      isDraft={false}
                      managementTypeOptions={managementTypeOptions}
                      ingredientOptions={ingredientOptions}
                      disabled={disabled}
                      lockStatus={lockStatus}
                      onSave={handleSaveWaste}
                      onCancel={() => setEditingId(null)}
                      onAddRecord={handleAddWasteRecord}
                    />
                  );
                }

                // Default: show the compact read-only card.
                return (
                  <WasteRecordCard
                    key={field.id}
                    field={field}
                    globalIndex={globalIndex}
                    recordData={wastes?.[globalIndex]}
                    managementTypeOptions={managementTypeOptions}
                    sideStreamUnit={sideStreamUnit}
                    onEdit={id => setEditingId(id)}
                    onDelete={(idx, id, name) => {
                      setWasteToDelete({ index: idx, id, name });
                      setDeleteModalOpen(true);
                    }}
                    disabled={disabled}
                    lockStatus={lockStatus}
                  />
                );
              })}

              {/* WastePagination renders null when totalPages <= 1 */}
              <WastePagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          )}

          {fields.length > 0 && !fields[fields.length - 1]._id && (
            <>
              {fields.length > 1 && <div className="tw-border-t tw-border-dashed tw-border-border" />}
              <div className="tw-space-y-2">
                <p className="tw-text-[10px] tw-text-muted-foreground">Add new waste record</p>
                <WasteRecordForm
                  key={fields[fields.length - 1].id}
                  control={control}
                  globalIndex={fields.length - 1}
                  field={fields[fields.length - 1]}
                  isDraft={true}
                  managementTypeOptions={managementTypeOptions}
                  ingredientOptions={ingredientOptions}
                  disabled={disabled}
                  lockStatus={lockStatus}
                  onSave={handleSaveWaste}
                  onCancel={() => {}}
                  onAddRecord={handleAddWasteRecord}
                />
              </div>
            </>
          )}
          </CardContent>
        </Form>
      )}

      {/* Delete confirmation modal — rendered at root level to avoid z-index issues */}
      {deleteModalOpen && wasteToDelete && (
        <ConfirmModal
          viewModal={deleteModalOpen}
          closeModal={() => {
            setDeleteModalOpen(false);
            setWasteToDelete(null);
          }}
          cancel={() => {
            setDeleteModalOpen(false);
            setWasteToDelete(null);
          }}
          approve={() => {
            handleRemoveWasteRecord(wasteToDelete.index, wasteToDelete.id);
            setDeleteModalOpen(false);
            setWasteToDelete(null);
          }}
          title="Delete Waste Record"
          subTitle={`Are you sure you want to remove "${wasteToDelete.name}"? This action cannot be undone.`}
          style={{ zIndex: 10001 }}
          className="tw-delete-confirmation-modal"
          mountNode={document.body}
        />
      )}
    </Card>
  );
};
